import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Monitor } from 'lucide-react';
import { FormTypeform } from '../FormTypeform';
import { FormLogo } from '../FormLogo';
import { SmartphoneFrame } from '../SmartphoneFrame';
import { MultiQuestionSlide } from '../MultiQuestionSlide';
import { renderFieldPreview } from '../../../lib/renderField';
import { getButtonStyles } from '../../../lib/themeUtils';
import { useThemeFont } from '../../../hooks/useThemeFont';
import type { FormField, ThemeConfig, Form } from '../../../types/forms';
import { DEFAULT_THEME_CONFIG } from '../../../types/forms';
import { getScreenAlignmentClass } from '../../../lib/getAlignmentClass';

interface SlidePreviewProps {
  field: FormField | null;
  themeConfig: ThemeConfig;
  viewportMode: 'desktop' | 'mobile';
  isPlayMode: boolean;
  form: Form;
  /** When `viewportMode='mobile'` in edit mode, controls whether the
   *  `<SmartphoneFrame>` mockup wraps the preview. Default `true` preserves
   *  current consumers (play mode, DesktopBuilder mobile toggle). Mobile builder
   *  passes `false` to render the form content directly inside a delimited canvas
   *  (story mobile-builder.7.3). Has no effect in play mode or desktop viewport. */
  showFrame?: boolean;
  children?: React.ReactNode;
}

const noopSubmit = async () => ({ error: null });

// Field types that render their own label internally — skip external label to avoid duplication
const SELF_LABELED_TYPES = ['welcome_screen', 'thank_you_screen', 'heading', 'paragraph', 'statement'];

// Desktop aspect ratio
const DESKTOP_RATIO = 16 / 9;

export const SlidePreview: React.FC<SlidePreviewProps> = ({
  field,
  themeConfig,
  viewportMode,
  isPlayMode,
  form,
  showFrame = true,
  children,
}) => {
  const theme = themeConfig || DEFAULT_THEME_CONFIG;
  useThemeFont(theme.fontFamily);

  const buttonStyles = getButtonStyles(theme);
  const isMobile = viewportMode === 'mobile';

  // Multi-field page detection: if selected field belongs to a page with multiple fields
  const pageForField = useMemo(() => {
    if (!field || !form.pages?.length) return null;
    const page = form.pages.find((p) => p.id === field.pageId);
    if (!page) return null;
    const pageFields = form.fields.filter((f) => f.pageId === page.id);
    return pageFields.length > 1 ? { page, fields: pageFields } : null;
  }, [field, form.pages, form.fields]);

  // Compute canvas size via ResizeObserver — desktop edit mode only
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || isPlayMode || isMobile) return;

    const computeSize = () => {
      const { width: cw, height: ch } = container.getBoundingClientRect();
      const availW = cw - 48;
      const availH = ch - 48;

      let w = availW;
      let h = w / DESKTOP_RATIO;

      if (h > availH) {
        h = availH;
        w = h * DESKTOP_RATIO;
      }

      setCanvasSize({ width: Math.round(w), height: Math.round(h) });
    };

    const observer = new ResizeObserver(computeSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isMobile, isPlayMode]);

  // Safe background image URL helper (Story saas-1.6)
  const bgImageUrl = useMemo(() => {
    const bg = field?.config?.backgroundImage;
    if (!bg) return null;
    try {
      const parsed = new URL(bg);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
      return encodeURI(bg);
    } catch {
      return null;
    }
  }, [field]);

  const bgBlur = field?.config?.backgroundBlur ?? 0;
  const bgOpacity = field?.config?.backgroundOpacity ?? 100;
  const logoMargin = form.settings?.logoMargin ?? 16;

  // Shared edit-mode content (field preview with label, render, button)
  const editContent = (
    <>
      {/* Logo absolute overlay (6 posições) */}
      <FormLogo
        logoUrl={form.settings?.logoUrl}
        logoPosition={form.settings?.logoPosition}
        logoMargin={logoMargin}
        logoScale={form.settings?.logoScale}
        variant="absolute"
      />

      {field ? (
        pageForField ? (
          /* Multi-field page preview */
          <div className={`w-full h-full ${isMobile ? 'p-4' : 'p-8'} overflow-auto`}>
            <MultiQuestionSlide
              fields={pageForField.fields}
              page={pageForField.page}
              themeConfig={theme}
              errors={{}}
              renderField={(f) => renderFieldPreview(f, theme)}
            />
          </div>
        ) : (
          /* Single-field preview */
          <div className={`relative w-full h-full flex items-center justify-center ${isMobile ? 'p-4' : 'p-8'} overflow-auto`}>
            {/* Camada absoluta de fundo — blur/opacity aplicam apenas aqui (Story saas-1.6) */}
            {bgImageUrl && (
              <div
                className="absolute inset-0 pointer-events-none z-0"
                style={{
                  backgroundImage: `url("${bgImageUrl}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: bgBlur > 0 ? `blur(${bgBlur}px)` : undefined,
                  opacity: bgOpacity / 100,
                }}
              />
            )}

            <div
              className={`relative z-10 w-full max-w-2xl space-y-6 ${getScreenAlignmentClass(field.type, field.alignment)}`}
              style={{ color: theme.textColor || '#ffffff' }}
            >
              {/* Logo inline — apenas para middle-center (Story saas-1.6) */}
              <FormLogo
                logoUrl={form.settings?.logoUrl}
                logoPosition={form.settings?.logoPosition}
                logoMargin={logoMargin}
                logoScale={form.settings?.logoScale}
                variant="inline"
              />

              {/* Field label + required indicator */}
              {!SELF_LABELED_TYPES.includes(field.type) && (
                <div>
                  <h2
                    className="text-2xl font-medium"
                    style={{ fontFamily: `'${theme.fontFamily}', sans-serif` }}
                  >
                    {field.label}
                    {field.required && (
                      <span className="text-red-400 ml-1">*</span>
                    )}
                  </h2>
                  {field.helpText && (
                    <p className="text-sm mt-2" style={{ opacity: 0.6 }}>{field.helpText}</p>
                  )}
                </div>
              )}

              {/* Field preview */}
              <div>{renderFieldPreview(field, theme)}</div>

              {/* Next button preview */}
              {!SELF_LABELED_TYPES.includes(field.type) && (
                <div>
                  <button
                    type="button"
                    disabled
                    className="font-medium cursor-default opacity-90"
                    style={buttonStyles}
                  >
                    {form.settings?.nextButtonText || 'OK'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4">
          <Monitor className="w-12 h-12" style={{ color: theme.textColor || '#ffffff', opacity: 0.2 }} />
          <p className="text-sm" style={{ color: theme.textColor || '#ffffff', opacity: 0.4 }}>
            Selecione um slide para visualizar
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className="flex-1 relative flex flex-col overflow-hidden bg-neutral-950">
      {/* Preview area — flex-1 min-h-0 lets it shrink to leave space for toolbar
          sibling. Replaces previous `sticky bottom-0` toolbar that overlaid the
          smartphone mockup (Bug 2 fix — hotfix preview-mobile-centering-and-frame-fit). */}
      <div className="flex-1 min-h-0 relative flex flex-col overflow-hidden">
        {isPlayMode ? (
          /* Play mode: SmartphoneFrame with active prop preserves FormTypeform state on toggle */
          <SmartphoneFrame active={isMobile}>
            <FormTypeform form={form} onSubmit={noopSubmit} isPreview />
          </SmartphoneFrame>
        ) : (
          /* Edit mode */
          isMobile ? (
            showFrame ? (
              <SmartphoneFrame>
                <div
                  className="relative w-full h-full"
                  style={{
                    backgroundColor: theme.backgroundColor,
                    fontFamily: `'${theme.fontFamily}', sans-serif`,
                  }}
                >
                  {editContent}
                </div>
              </SmartphoneFrame>
            ) : (
              /* No-frame mobile edit (mobile-builder.7.3): content renders direct,
                 delimited externally by the canvas wrapper. Scroll lives here so
                 long forms remain navigable inside the bounded canvas area. */
              <div
                className="relative flex-1 min-h-0 w-full overflow-y-auto"
                style={{
                  backgroundColor: theme.backgroundColor,
                  fontFamily: `'${theme.fontFamily}', sans-serif`,
                }}
              >
                {editContent}
              </div>
            )
          ) : (
            <div
              ref={containerRef}
              className="flex-1 flex items-center justify-center p-6"
            >
              {canvasSize.width > 0 && (
                <div
                  className="relative rounded-xl overflow-hidden shadow-2xl border border-white/6"
                  style={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    backgroundColor: theme.backgroundColor,
                    fontFamily: `'${theme.fontFamily}', sans-serif`,
                  }}
                >
                  {editContent}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Children slot (SlideToolbar) — flex-shrink-0 reserves its own space
          so the preview area above never overlaps it. */}
      {children && (
        <div className="flex-shrink-0">
          {children}
        </div>
      )}
    </div>
  );
};
